import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MessageCircle, Calculator, Calendar, Gauge, Cog, Fuel, BadgeCheck, Tag, Share2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { formatBRL, formatKM, waLink } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VehicleDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setV(null);
    setIdx(0);
    axios.get(`${API}/vehicles/${id}`).then((r) => setV(r.data)).catch(() => setError(true));
  }, [id]);

  const fotos = v ? ((v.fotos && v.fotos.length ? v.fotos : [v.imagem]).filter(Boolean)) : [];

  useEffect(() => {
    if (v) document.title = `${v.nome} ${v.ano} | Seed Motors`;
  }, [v]);

  const shareAd = async () => {
    const shareUrl = `${API}/share/veiculo/${v.id}`;
    const data = { title: `${v.nome} | Seed Motors`, text: `${v.nome} ${v.ano} — ${formatBRL(v.preco)}`, url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(data); } catch { /* cancelado */ }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link de compartilhamento copiado! Cole no WhatsApp e a foto do carro aparece na prévia.");
    }
  };

  return (
    <div className="min-h-screen bg-[#07090B]" data-testid="vehicle-detail-page">
      <header className="glass border-b border-slate-800/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="group" data-testid="detail-logo"><Logo /></a>
          <a
            href={waLink(v ? `Olá! Quero fazer uma proposta no ${v.nome} ${v.ano} (${formatBRL(v.preco)}).` : "Olá!")}
            target="_blank" rel="noopener noreferrer"
            data-testid="detail-whatsapp-button"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Falar no WhatsApp
          </a>
        </div>
      </header>

      {error ? (
        <div className="max-w-7xl mx-auto px-4 py-32 text-center">
          <p className="font-display text-2xl font-bold text-white mb-4">Veículo não encontrado</p>
          <a href="/#estoque" className="text-emerald-400 font-semibold" data-testid="detail-back-link">← Voltar ao estoque</a>
        </div>
      ) : !v ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-2 gap-10">
          <div className="h-[420px] rounded-3xl bg-[#131620] animate-pulse" />
          <div className="space-y-4"><div className="h-10 w-2/3 rounded-xl bg-[#131620] animate-pulse" /><div className="h-6 w-1/3 rounded-xl bg-[#131620] animate-pulse" /></div>
        </div>
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <a href="/#estoque" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors mb-8" data-testid="detail-back-link">
            <ArrowLeft className="w-4 h-4" /> Voltar ao estoque
          </a>

          <div className="grid lg:grid-cols-2 gap-10">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="relative rounded-3xl overflow-hidden bg-[#131620] border border-slate-800/60 aspect-[4/3]" data-testid="detail-gallery">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={fotos[idx]}
                    src={fotos[idx]}
                    alt={v.nome}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45 }}
                    className="w-full h-full object-cover"
                    data-testid="detail-main-image"
                  />
                </AnimatePresence>
                {v.destaque && (
                  <span className="absolute top-5 left-5 bg-emerald-500 text-emerald-950 text-xs font-bold px-3 py-1.5 rounded-full">DESTAQUE</span>
                )}
              </div>
              {fotos.length > 1 && (
                <div className="mt-4 grid grid-cols-5 gap-3" data-testid="detail-thumbs">
                  {fotos.map((f, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      data-testid={`detail-thumb-${i}`}
                      className={`rounded-xl overflow-hidden aspect-[4/3] border-2 transition-colors ${i === idx ? "border-emerald-500" : "border-transparent opacity-60 hover:opacity-100"}`}
                    >
                      <img src={f} alt={`${v.nome} foto ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="flex flex-col">
              <p className="text-xs font-mono2 tracking-widest uppercase text-emerald-400 mb-2">{v.marca} · {v.categoria}</p>
              <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white" data-testid="detail-title">{v.nome}</h1>

              <p className="mt-6 font-display text-4xl font-extrabold text-emerald-400" data-testid="detail-price">{formatBRL(v.preco)}</p>

              <div className="mt-8 grid grid-cols-2 gap-3" data-testid="detail-specs">
                {[
                  { icon: Calendar, label: "Ano", value: v.ano },
                  { icon: Gauge, label: "Quilometragem", value: `${formatKM(v.km)} km` },
                  { icon: Cog, label: "Câmbio", value: v.cambio },
                  { icon: Fuel, label: "Combustível", value: v.combustivel },
                ].map((s) => (
                  <div key={s.label} className="glass rounded-2xl p-4 flex items-center gap-3">
                    <s.icon className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wide">{s.label}</p>
                      <p className="text-sm font-semibold text-white">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 glass rounded-2xl p-4 flex items-center gap-3 text-sm text-slate-300">
                <BadgeCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                Vistoria cautelar aprovada · procedência garantida Seed Motors
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink(`Olá! Quero fazer uma proposta no ${v.nome} ${v.ano} (${formatBRL(v.preco)}). Link: ${window.location.href}`)}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="detail-proposal-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors shadow-[0_0_28px_rgba(31,206,109,0.35)]"
                >
                  <Tag className="w-5 h-5" /> Fazer proposta
                </a>
                <a
                  href="/#simulador"
                  data-testid="detail-simulator-link"
                  className="flex-1 inline-flex items-center justify-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-6 py-4 rounded-full transition-colors"
                >
                  <Calculator className="w-5 h-5" /> Simular financiamento
                </a>
                <button
                  onClick={shareAd}
                  data-testid="detail-share-button"
                  title="Compartilhar anúncio"
                  className="inline-flex items-center justify-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-5 py-4 rounded-full transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </div>
        </main>
      )}

      <div className="mt-16"><Footer /></div>
      <WhatsAppFloat />
    </div>
  );
}
